import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  Security,
  Add,
  Edit,
  Delete,
  Save,
} from '@mui/icons-material';

interface CommissionStructure {
  id: string;
  serviceCategory: string;
  commissionType: 'percentage' | 'fixed';
  value: number;
  minThreshold?: number;
}

interface PerformanceMetric {
  id: string;
  name: string;
  type: 'revenue' | 'bookings' | 'rating' | 'retention';
  target: number;
  weight: number;
  enabled: boolean;
}

interface StaffManagementSettingsProps {
  salonId: string;
}

const StaffManagementSettings: React.FC<StaffManagementSettingsProps> = ({ salonId }) => {
  const [commissionSettings, setCommissionSettings] = useState({
    enableCommissions: true,
    defaultCommissionRate: 15,
    commissionCalculation: 'net_revenue', // 'gross_revenue' | 'net_revenue'
    payoutFrequency: 'monthly', // 'weekly' | 'bi_weekly' | 'monthly'
    minimumPayout: 500,
  });

  const [commissionStructures, setCommissionStructures] = useState<CommissionStructure[]>([
    {
      id: '1',
      serviceCategory: 'Bridal Makeup',
      commissionType: 'percentage',
      value: 20,
      minThreshold: 5000,
    },
    {
      id: '2',
      serviceCategory: 'Hair Styling',
      commissionType: 'percentage',
      value: 15,
    },
    {
      id: '3',
      serviceCategory: 'Facial Treatment',
      commissionType: 'fixed',
      value: 200,
    },
  ]);

  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetric[]>([
    {
      id: '1',
      name: 'Monthly Revenue',
      type: 'revenue',
      target: 50000,
      weight: 40,
      enabled: true,
    },
    {
      id: '2',
      name: 'Customer Bookings',
      type: 'bookings',
      target: 100,
      weight: 30,
      enabled: true,
    },
    {
      id: '3',
      name: 'Customer Rating',
      type: 'rating',
      target: 4.5,
      weight: 20,
      enabled: true,
    },
    {
      id: '4',
      name: 'Customer Retention',
      type: 'retention',
      target: 80,
      weight: 10,
      enabled: true,
    },
  ]);

  const [shiftSettings, setShiftSettings] = useState({
    enableShiftManagement: true,
    maxHoursPerDay: 8,
    maxHoursPerWeek: 40,
    overtimeRate: 1.5,
    breakDuration: 60, // minutes
    minTimeBetweenShifts: 12, // hours
  });

  const [accessSettings, setAccessSettings] = useState({
    enableRoleBasedAccess: true,
    sessionTimeout: 480, // minutes
    requirePasswordChange: true,
    passwordChangeInterval: 90, // days
    enableTwoFactor: false,
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleCommissionSettingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setCommissionSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleShiftSettingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setShiftSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleAccessSettingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setAccessSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleMetricToggle = (metricId: string) => {
    setPerformanceMetrics(prev => prev.map(metric => 
      metric.id === metricId ? { ...metric, enabled: !metric.enabled } : metric
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2D3748', mb: 3 }}>
        Staff Management Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Staff management settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Commission Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUp sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Commission Structure
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={commissionSettings.enableCommissions}
                        onChange={handleCommissionSettingChange('enableCommissions')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Commission System"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Default Commission Rate (%)"
                    type="number"
                    value={commissionSettings.defaultCommissionRate}
                    onChange={handleCommissionSettingChange('defaultCommissionRate')}
                    disabled={!commissionSettings.enableCommissions}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!commissionSettings.enableCommissions}>
                    <InputLabel>Commission Calculation</InputLabel>
                    <Select
                      value={commissionSettings.commissionCalculation}
                      label="Commission Calculation"
                      onChange={(e) => setCommissionSettings(prev => ({ ...prev, commissionCalculation: e.target.value }))}
                    >
                      <MenuItem value="gross_revenue">Gross Revenue</MenuItem>
                      <MenuItem value="net_revenue">Net Revenue (After Discounts)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth disabled={!commissionSettings.enableCommissions}>
                    <InputLabel>Payout Frequency</InputLabel>
                    <Select
                      value={commissionSettings.payoutFrequency}
                      label="Payout Frequency"
                      onChange={(e) => setCommissionSettings(prev => ({ ...prev, payoutFrequency: e.target.value }))}
                    >
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="bi_weekly">Bi-Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Minimum Payout (₹)"
                    type="number"
                    value={commissionSettings.minimumPayout}
                    onChange={handleCommissionSettingChange('minimumPayout')}
                    disabled={!commissionSettings.enableCommissions}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Shift Management */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Schedule sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Shift Management
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={shiftSettings.enableShiftManagement}
                        onChange={handleShiftSettingChange('enableShiftManagement')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Shift Management"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Hours Per Day"
                    type="number"
                    value={shiftSettings.maxHoursPerDay}
                    onChange={handleShiftSettingChange('maxHoursPerDay')}
                    disabled={!shiftSettings.enableShiftManagement}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Hours Per Week"
                    type="number"
                    value={shiftSettings.maxHoursPerWeek}
                    onChange={handleShiftSettingChange('maxHoursPerWeek')}
                    disabled={!shiftSettings.enableShiftManagement}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Overtime Rate Multiplier"
                    type="number"
                    value={shiftSettings.overtimeRate}
                    onChange={handleShiftSettingChange('overtimeRate')}
                    disabled={!shiftSettings.enableShiftManagement}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Break Duration (minutes)"
                    type="number"
                    value={shiftSettings.breakDuration}
                    onChange={handleShiftSettingChange('breakDuration')}
                    disabled={!shiftSettings.enableShiftManagement}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Min Time Between Shifts (hours)"
                    type="number"
                    value={shiftSettings.minTimeBetweenShifts}
                    onChange={handleShiftSettingChange('minTimeBetweenShifts')}
                    disabled={!shiftSettings.enableShiftManagement}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Commission Structures Table */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Service Commission Rates
                </Typography>
                <Button
                  startIcon={<Add />}
                  variant="outlined"
                  sx={{
                    borderColor: '#E8B4B8',
                    color: '#E8B4B8',
                    '&:hover': {
                      borderColor: '#D69BA0',
                      bgcolor: 'rgba(232, 180, 184, 0.05)',
                    },
                  }}
                >
                  Add Commission Rule
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Service Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Commission Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rate/Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Min Threshold</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commissionStructures.map((structure) => (
                      <TableRow key={structure.id}>
                        <TableCell>{structure.serviceCategory}</TableCell>
                        <TableCell>
                          <Chip 
                            label={structure.commissionType} 
                            size="small" 
                            color={structure.commissionType === 'percentage' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>
                          {structure.commissionType === 'percentage' 
                            ? `${structure.value}%` 
                            : `₹${structure.value}`
                          }
                        </TableCell>
                        <TableCell>
                          {structure.minThreshold ? `₹${structure.minThreshold}` : '-'}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" sx={{ color: '#E8B4B8', mr: 1 }}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUp sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Performance Metrics
                </Typography>
              </Box>

              <List>
                {performanceMetrics.map((metric) => (
                  <ListItem key={metric.id} divider>
                    <ListItemText
                      primary={metric.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Target: {metric.type === 'revenue' ? `₹${metric.target.toLocaleString()}` : 
                                   metric.type === 'rating' ? `${metric.target}/5` :
                                   metric.type === 'retention' ? `${metric.target}%` : metric.target}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Weight: {metric.weight}%
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={metric.enabled}
                        onChange={() => handleMetricToggle(metric.id)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Access Control */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Access Control
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accessSettings.enableRoleBasedAccess}
                        onChange={handleAccessSettingChange('enableRoleBasedAccess')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Role-Based Access Control"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Session Timeout (minutes)"
                    type="number"
                    value={accessSettings.sessionTimeout}
                    onChange={handleAccessSettingChange('sessionTimeout')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accessSettings.requirePasswordChange}
                        onChange={handleAccessSettingChange('requirePasswordChange')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Require Periodic Password Change"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password Change Interval (days)"
                    type="number"
                    value={accessSettings.passwordChangeInterval}
                    onChange={handleAccessSettingChange('passwordChangeInterval')}
                    disabled={!accessSettings.requirePasswordChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accessSettings.enableTwoFactor}
                        onChange={handleAccessSettingChange('enableTwoFactor')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enforce Two-Factor Authentication"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: '#E8B4B8',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 600,
                '&:hover': { bgcolor: '#D69BA0' },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StaffManagementSettings;